import { useRouter } from 'next/router';
import React from 'react';

import type { NavItemInternal, NavItem, NavGroupItem } from 'types/client/navigation';
import config from 'configs/app';
import { rightLineArrow } from 'toolkit/utils/htmlEntities';

interface ReturnType {
  mainNavItems: Array<NavItem>;
  accountNavItems: Array<NavItem>;
}

/**
 * IMPORTANT:
 * Navigation UI still calls isGroupItem().
 * Since we removed sub-menus, this must ALWAYS return false.
 */
export function isGroupItem(_item: NavItem | NavGroupItem): _item is NavGroupItem {
  return false;
}

export function isInternalItem(item: NavItem): item is NavItemInternal {
  return 'nextRoute' in item;
}

export default function useNavItems(): ReturnType {
  const router = useRouter();
  const pathname = router.pathname;
  const query = router.query;
  const tab = query.tab;

  return React.useMemo(() => {
    /* -------------------------
     * CORE ITEMS
     * ------------------------- */
    const wallet: NavItem = {
      text: `Wallet`,
      nextRoute: { pathname: '/wallet' as const },
      icon: 'wallet', // make sure this icon exists in IconSvg
      isActive:
        pathname === '/wallet' ||
        pathname.startsWith('/wallet/'),
    };

    const walletBond: NavItem = {
      text: `Wallet Bond`,
      nextRoute: { pathname: '/bondWallet' as const },
      icon: 'wallet', // make sure this icon exists in IconSvg
      isActive:
        pathname === '/bondWallet' ||
        pathname.startsWith('/bondWallet/'),
    };
    
    const coinMarketCap: NavItem = {
      text: `CoinMarketCao`,
      nextRoute: { pathname: '/coinMarketCap' as const },
      icon: 'wallet', // make sure this icon exists in IconSvg
      isActive:
        pathname === '/coinMarketCap' ||
        pathname.startsWith('/coinMarketCap/'),
    };
    
    const dashboard: NavItem = {
      text: 'Dashboard',
      nextRoute: { pathname: '/dashboard' as const },
      icon: 'home',
      isActive: pathname.startsWith('/dashboard'),
    };

    const txs: NavItem = {
      text: 'Transactions',
      nextRoute: { pathname: '/txs' as const },
      icon: 'transactions',
      isActive:
        (pathname === '/txs' &&
          (!config.features.zetachain.isEnabled || !tab || !tab.includes('cctx'))) ||
        pathname === '/tx/[hash]' ||
        pathname === '/chain/[chain-slug]/tx/[hash]',
    };

    const blocks: NavItem = {
      text: 'Blocks',
      nextRoute: { pathname: '/blocks' as const },
      icon: 'block',
      isActive:
        pathname === '/blocks' ||
        pathname === '/block/[height_or_hash]' ||
        pathname === '/chain/[chain-slug]/block/[height_or_hash]',
    };

    const internalTxs: NavItem = {
      text: 'Internal transactions',
      nextRoute: { pathname: '/internal-txs' as const },
      icon: 'internal_txns',
      isActive: pathname === '/internal-txs',
    };

    const verifiedContracts: NavItem = {
      text: 'Verified contracts',
      nextRoute: { pathname: '/verified-contracts' as const },
      icon: 'verified',
      isActive: pathname === '/verified-contracts',
    };

    const tokens: NavItem = {
      text: 'Tokens',
      nextRoute: { pathname: '/tokens' as const },
      icon: 'token',
      isActive: pathname === '/tokens' || pathname.startsWith('/token/'),
    };

    const tokenTransfers: NavItem = {
      text: 'Token transfers',
      nextRoute: { pathname: '/token-transfers' as const },
      icon: 'token-transfers',
      isActive: pathname === '/token-transfers',
    };

    const stats: NavItem | null = config.features.stats.isEnabled
      ? {
          text: 'Stats',
          nextRoute: { pathname: '/stats' as const },
          icon: 'stats',
          isActive: pathname.startsWith('/stats'),
        }
      : null;

    // const api: NavItem | null = config.features.apiDocs.isEnabled
    //   ? {
    //       text: 'API',
    //       nextRoute: { pathname: '/api-docs' as const },
    //       icon: 'restAPI',
    //       isActive: pathname.startsWith('/api-docs'),
    //     }
    //   : null;

    /* -------------------------
     * MAIN NAV (FLAT)
     * ------------------------- */

    const mainNavItems: NavItem[] = [
      dashboard,
      txs,
      blocks,
      internalTxs,
      tokens,
      tokenTransfers,
      verifiedContracts,
      stats,
      wallet,
      walletBond,
      coinMarketCap,
      // api,
    ].filter(Boolean) as NavItem[];

    /* -------------------------
     * ACCOUNT NAV (UNCHANGED)
     * ------------------------- */

    const accountNavItems: NavItem[] = [
      {
        text: 'Watch list',
        nextRoute: { pathname: '/account/watchlist' as const },
        icon: 'watchlist',
        isActive: pathname === '/account/watchlist',
      },
      {
        text: 'Private tags',
        nextRoute: { pathname: '/account/tag-address' as const },
        icon: 'privattags',
        isActive: pathname === '/account/tag-address',
      },
      {
        text: 'API keys',
        nextRoute: { pathname: '/account/api-key' as const },
        icon: 'API',
        isActive: pathname === '/account/api-key',
      },
    ];

    return { mainNavItems, accountNavItems };
  }, [ pathname, tab ]);
}
